import {FC, useMemo, useState} from "react";
import {Alert, Button, Col, Container, FormSelect, InputGroup, Row, Spinner, Tab, Tabs} from "react-bootstrap";
import ReactDatePicker from "react-datepicker";
import {collection, getDocs, query, QueryConstraint, where} from "firebase/firestore";
import {useCollectionData} from "react-firebase-hooks/firestore";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCalendar} from "@fortawesome/free-solid-svg-icons";
import {appFirestore} from "../../../../../index";
import {dateTimeToString, dateToString, dateWithMaxTime, dateWithZeroTime, hoursAsShortString} from "../../../../tools";
import {MaybeDate} from "../../../../types/Maybe";
import {employeesFBConverter} from "../../../employees/EmployeesFBConverter";
import {logisticsFBConverter} from "../../LogisticsFBConverter";
import {mergeLogisticsById} from "../transport-work-report/LogisticsWorkReportComputing";
import {RRLogisticType} from "../../RRLogisticType";
import RRTable from "../../../../components/rr-table/RRTable";

type ReportMonthOption = {
    label: string;
    value: string;
    periodFrom: Date;
    periodTo: Date;
};

type HoursDiscrepancyItem = {
    id: string;
    driverId: string;
    driverName: string;
    plannedStartDate: Date;
    plannedEndDate: Date;
    reportedStartDate: Date | null;
    reportedEndDate: Date | null;
    plannedHours: number;
    reportedHours: number;
    differenceHours: number;
    activity: string;
    reason: string;
};

type DriverReportTab = {
    id: string;
    label: string;
};

const ALL_DRIVERS_TAB_KEY = "all";

const BG_MONTH_NAMES = [
    "януари", "февруари", "март", "април", "май", "юни",
    "юли", "август", "септември", "октомври", "ноември", "декември",
];

function createMonthOption(baseDate: Date): ReportMonthOption {
    const periodFrom = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
    const periodTo = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0);

    return {
        label: `${BG_MONTH_NAMES[periodFrom.getMonth()]}, ${periodFrom.getFullYear()}`,
        value: `${periodFrom.getFullYear()}-${String(periodFrom.getMonth() + 1).padStart(2, "0")}`,
        periodFrom,
        periodTo,
    };
}

function getMonthOptions(today: Date): ReportMonthOption[] {
    return Array.from({length: 13}, (_, index) =>
        createMonthOption(new Date(today.getFullYear(), today.getMonth() - index, 1))
    );
}

/**
 * Връща продължителността на интервала в часове. Равните часове са нулева
 * продължителност, а само краен час преди началния означава работа до следващия ден.
 */
function calculateIntervalHours(startDate: Date, endDate: Date): number {
    const effectiveEndDate = endDate < startDate
        ? new Date(endDate.getTime() + 24 * 60 * 60 * 1000)
        : endDate;

    return (effectiveEndDate.getTime() - startDate.getTime()) / (60 * 60 * 1000);
}

function getActivity(logistic: RRLogisticType): string {
    if (logistic.workInStore) {
        return "Склад";
    }

    return logistic.eventNumber.trim().length > 0
        ? `Събитие № ${logistic.eventNumber.trim()}`
        : "Без събитие";
}

function formatDifference(hours: number): string {
    if (hours === 0) {
        return "0 ч";
    }

    return `${hours > 0 ? "+" : "−"}${hoursAsShortString(Math.abs(hours))}`;
}

function formatHoursTotal(hours: number): string {
    return hoursAsShortString(hours) || "0ч";
}

function formatDriverTabLabel(driverName: string): string {
    const nameParts = driverName.trim().split(/\s+/).filter(Boolean);
    if (nameParts.length <= 2) {
        return nameParts.join(" ");
    }

    return `${nameParts[0]} ${nameParts[nameParts.length - 1]}`;
}

const LogisticsHoursDiscrepancyReportView: FC = () => {
    const today = dateWithZeroTime(new Date())!;
    const monthOptions = useMemo(() => getMonthOptions(today), [today]);
    const defaultMonthOption = monthOptions[1] ?? monthOptions[0];

    const [selectedMonth, setSelectedMonth] = useState(defaultMonthOption.value);
    const [periodFrom, setPeriodFrom] = useState<MaybeDate>(defaultMonthOption.periodFrom);
    const [periodTo, setPeriodTo] = useState<MaybeDate>(defaultMonthOption.periodTo);
    const [reportItems, setReportItems] = useState<HoursDiscrepancyItem[] | undefined>();
    const [selectedDriverId, setSelectedDriverId] = useState(ALL_DRIVERS_TAB_KEY);
    const [working, setWorking] = useState(false);
    const [hasLoadingError, setHasLoadingError] = useState(false);

    const employeesQuery = useMemo(() => query(
        collection(appFirestore, "employees"),
        where("isLogistics", "==", true),
        where("isEmployee", "==", true)
    ).withConverter(employeesFBConverter), []);
    const [employees, loadingEmployees, employeesError] = useCollectionData(employeesQuery);

    const calculateReport = async (rangeStart: Date, rangeEnd: Date): Promise<void> => {
        if (!employees) {
            setReportItems([]);
            return;
        }

        const logisticsCollection = collection(appFirestore, "logistics").withConverter(logisticsFBConverter);
        const getConstraints = (dateField: "startDate" | "reportStartDate"): QueryConstraint[] => [
            where(dateField, ">=", rangeStart),
            where(dateField, "<=", rangeEnd),
        ];
        const [plannedStarts, reportedStarts] = await Promise.all([
            getDocs(query(logisticsCollection, ...getConstraints("startDate"))),
            getDocs(query(logisticsCollection, ...getConstraints("reportStartDate"))),
        ]);
        const logistics = mergeLogisticsById(
            plannedStarts.docs.map(document => document.data()),
            reportedStarts.docs.map(document => document.data())
        );
        const driverNamesById = new Map(employees.map(employee => [employee.id, employee.name]));

        const discrepancies = logistics.flatMap(logistic => {
            // Складовите записи се изчисляват по планирания си период и не са разминаване.
            if (logistic.dayType !== "WORKDAY" || logistic.workInStore || !logistic.id || !driverNamesById.has(logistic.driverId)) {
                return [];
            }

            const plannedHours = calculateIntervalHours(logistic.startDate, logistic.endDate);
            const reportedStartDate = logistic.reportStartDate;
            const reportedEndDate = logistic.reportEndDate;
            const hasReportedPeriod = reportedStartDate !== null && reportedEndDate !== null;
            const reportedHours = reportedStartDate && reportedEndDate
                ? calculateIntervalHours(reportedStartDate, reportedEndDate)
                : 0;
            const dateChanged = reportedStartDate !== null &&
                logistic.startDate.toDateString() !== reportedStartDate.toDateString();
            const differenceHours = reportedHours - plannedHours;

            if (hasReportedPeriod && differenceHours === 0 && !dateChanged) {
                return [];
            }

            const reason = !hasReportedPeriod
                ? "Липсва отчетен период"
                : dateChanged && differenceHours === 0
                    ? "Различна отчетена дата"
                    : "Различни часове";

            const item: HoursDiscrepancyItem = {
                id: logistic.id,
                driverId: logistic.driverId,
                driverName: driverNamesById.get(logistic.driverId) ?? logistic.driverName,
                plannedStartDate: logistic.startDate,
                plannedEndDate: logistic.endDate,
                reportedStartDate,
                reportedEndDate,
                plannedHours,
                reportedHours,
                differenceHours,
                activity: getActivity(logistic),
                reason,
            };

            return [item];
        }).sort((first, second) => {
            const byDriver = first.driverName.localeCompare(second.driverName, "bg");
            return byDriver !== 0
                ? byDriver
                : first.plannedStartDate.getTime() - second.plannedStartDate.getTime();
        });

        setReportItems(discrepancies);
        setSelectedDriverId(ALL_DRIVERS_TAB_KEY);
    };

    const runReport = (): void => {
        if (!periodFrom || !periodTo || !employees) {
            return;
        }

        const rangeStart = dateWithZeroTime(periodFrom);
        const rangeEnd = dateWithMaxTime(periodTo);
        if (!rangeStart || !rangeEnd) {
            return;
        }

        setHasLoadingError(false);
        setWorking(true);
        void calculateReport(rangeStart, rangeEnd).then(
            () => setWorking(false),
            () => {
                setHasLoadingError(true);
                setWorking(false);
            }
        );
    };

    const changeMonth = (monthValue: string): void => {
        const monthOption = monthOptions.find(option => option.value === monthValue);
        if (!monthOption) {
            return;
        }

        setSelectedMonth(monthValue);
        setPeriodFrom(monthOption.periodFrom);
        setPeriodTo(monthOption.periodTo);
    };

    const driverTabs = useMemo<DriverReportTab[]>(() => {
        const driversById = new Map<string, DriverReportTab>();
        reportItems?.forEach(item => {
            if (!driversById.has(item.driverId)) {
                driversById.set(item.driverId, {
                    id: item.driverId,
                    label: formatDriverTabLabel(item.driverName),
                });
            }
        });

        return Array.from(driversById.values()).sort((first, second) =>
            first.label.localeCompare(second.label, "bg")
        );
    }, [reportItems]);

    const visibleReportItems = useMemo(() => reportItems?.filter(item =>
        selectedDriverId === ALL_DRIVERS_TAB_KEY || item.driverId === selectedDriverId
    ) ?? [], [reportItems, selectedDriverId]);

    const totalPlannedHours = visibleReportItems.reduce((total, item) => total + item.plannedHours, 0);
    const totalReportedHours = visibleReportItems.reduce((total, item) => total + item.reportedHours, 0);

    return <Container fluid>
        <h1>Справки и отчети</h1>
        <h3>Разминавания между планирани и отчетени часове</h3>

        <Row className="g-2 mt-3 flex-nowrap">
            <Col>
                <Row className="lh-2-5em flex-nowrap">
                    <Col xs="auto">
                        <Row>
                            <Col xs="auto" className="lh-2-5em">Месец:</Col>
                            <Col xs="auto" className="d-inline-block">
                                <FormSelect
                                    className="mb-2"
                                    disabled={working}
                                    value={selectedMonth}
                                    onChange={event => changeMonth(event.target.value)}
                                >
                                    {monthOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                                </FormSelect>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs="auto">
                        <Row>
                            <Col xs="auto" className="lh-2-5em">Начална дата:</Col>
                            <Col xs="auto" className="d-inline-block">
                                <InputGroup className="mb-2 w-180px flex-nowrap">
                                    <ReactDatePicker
                                        disabled={working}
                                        id="hoursDiscrepancyPeriodFromDate"
                                        className="form-control flex-nowrap"
                                        selected={periodFrom}
                                        onChange={date => setPeriodFrom(date || defaultMonthOption.periodFrom)}
                                        value={dateToString(periodFrom)}
                                        maxDate={periodTo}
                                    />
                                    <label htmlFor="hoursDiscrepancyPeriodFromDate">
                                        <InputGroup.Text className="lh-2-5em">
                                            <FontAwesomeIcon icon={faCalendar} size="xl"/>
                                        </InputGroup.Text>
                                    </label>
                                </InputGroup>
                            </Col>
                        </Row>
                    </Col>
                    <Col xs="auto">
                        <Row>
                            <Col xs="auto" className="lh-2-5em">Крайна дата:</Col>
                            <Col xs="auto" className="d-inline-block">
                                <InputGroup className="mb-2 w-180px flex-nowrap">
                                    <ReactDatePicker
                                        disabled={working}
                                        id="hoursDiscrepancyPeriodToDate"
                                        className="form-control flex-nowrap"
                                        selected={periodTo}
                                        onChange={date => setPeriodTo(date || today)}
                                        value={dateToString(periodTo)}
                                        minDate={periodFrom}
                                    />
                                    <label htmlFor="hoursDiscrepancyPeriodToDate">
                                        <InputGroup.Text className="lh-2-5em">
                                            <FontAwesomeIcon icon={faCalendar} size="xl"/>
                                        </InputGroup.Text>
                                    </label>
                                </InputGroup>
                            </Col>
                        </Row>
                    </Col>
                    <Col>
                        <Button onClick={runReport} disabled={working || loadingEmployees}>Изчисли</Button>
                    </Col>
                </Row>
            </Col>
        </Row>

        {loadingEmployees || working ? <Spinner animation="border"/> : null}
        {employeesError ? <Alert variant="danger">Грешка при зареждане на служителите.</Alert> : null}
        {hasLoadingError ? <Alert variant="danger">Грешка при изчисляване на справката.</Alert> : null}

        {reportItems !== undefined && !working && !hasLoadingError ? <>
            <Tabs
                activeKey={selectedDriverId}
                className="mb-3 flex-nowrap overflow-auto"
                id="hours-discrepancy-drivers"
                onSelect={selectedKey => setSelectedDriverId(selectedKey ?? ALL_DRIVERS_TAB_KEY)}
            >
                <Tab eventKey={ALL_DRIVERS_TAB_KEY} title="Всички"/>
                {driverTabs.map(driver => <Tab key={driver.id} eventKey={driver.id} title={driver.label}/>) }
            </Tabs>
            <Row className="g-2 mb-3">
                <Col xs="auto"><strong>Планирани часове: {formatHoursTotal(totalPlannedHours)}</strong></Col>
                <Col xs="auto"><strong>Отчетени часове: {formatHoursTotal(totalReportedHours)}</strong></Col>
            </Row>
            <RRTable>
                <thead>
                <tr>
                    <th>Шофьор</th>
                    <th>Планиран период</th>
                    <th>Отчетен период</th>
                    <th>Планирани часове</th>
                    <th>Отчетени часове</th>
                    <th>Разлика</th>
                    <th>Дейност</th>
                    <th>Причина</th>
                </tr>
                </thead>
                <tbody>
                {visibleReportItems.map(item => <tr key={item.id}>
                    <td>{item.driverName}</td>
                    <td>{dateTimeToString(item.plannedStartDate)} – {dateTimeToString(item.plannedEndDate)}</td>
                    <td>
                        {item.reportedStartDate && item.reportedEndDate
                            ? `${dateTimeToString(item.reportedStartDate)} – ${dateTimeToString(item.reportedEndDate)}`
                            : "—"}
                    </td>
                    <td className="text-end">{hoursAsShortString(item.plannedHours)}</td>
                    <td className="text-end">{hoursAsShortString(item.reportedHours)}</td>
                    <td className="text-end fw-bold">{formatDifference(item.differenceHours)}</td>
                    <td>{item.activity}</td>
                    <td>{item.reason}</td>
                </tr>)}
                {visibleReportItems.length === 0 ? <tr>
                    <td colSpan={8} className="text-center">Няма разминавания за избрания период.</td>
                </tr> : null}
                </tbody>
            </RRTable>
        </> : null}
    </Container>;
};

export default LogisticsHoursDiscrepancyReportView;
